import {
  Alert,
  Button,
  Callout,
  Card,
  FormGroup,
  InputGroup,
  ProgressBar,
  Switch,
} from "@blueprintjs/core";
import {
  DateInput,
  DateRangeInput,
  TimezoneSelect,
  type DateRangeInputProps,
} from "@blueprintjs/datetime";
import { HeaderCreateEdit, useToaster } from "@package/ui-components";
import {
  CreateScheduledEventDocument,
  DeleteScheduledEventDocument,
  GetScheduledEventDocument,
  GetScheduledEventsDocument,
  ScheduledEventModificationType,
  UpdateScheduledEventDocument,
  useGetScheduledEventQuery,
  type ScheduledEvent,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import { produce, type WritableDraft } from "immer";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  EDIT_CARD_WIDTH_CLASS,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { useAppSelector } from "../../../../store/store";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";
import {
  BROWSER_TIMEZONE,
  createScheduledEventMutationVariables,
  updateScheduledEventMutationVariables,
} from "./calendar-utils";
import { SchedulerRecurrenceEditor } from "./recurrence/SchedulerRecurrenceEditor";
import { SchedulerRecurrenceSummary } from "./recurrence/SchedulerRecurrenceSummary";
import { SchedulerEditActionList } from "./SchedulerEditAction";
import { SchedulerTimezoneNote } from "./SchedulerTimezoneNote";
import { useDeleteScheduledEventGroup } from "./use-delete-scheduled-event-group";

type SchedulerEditProps = {
  modifyType: ScheduledEventModificationType;
};

const defaultScheduledEvent: ScheduledEvent = {
  start: moment().startOf("day").toDate(),
  name: "",
  organization: "",
  startProcessed: false,
  endProcessed: false,
  timezone: BROWSER_TIMEZONE,
};

export function SchedulerEdit({
  modifyType = ScheduledEventModificationType.Single,
}: SchedulerEditProps) {
  const [event, setEvent] = useState<ScheduledEvent>(defaultScheduledEvent);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [isDeleteSeriesAlertOpen, setIsDeleteSeriesAlertOpen] =
    useState<boolean>(false);
  const { id } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { toaster } = useToaster();
  const { deleteGroup, deleting: deletingGroup } =
    useDeleteScheduledEventGroup();
  const argsCreate = useAppSelector((state) => state.scheduler.argsCreate);

  const eventCreateArgs = useMemo(
    () => createScheduledEventMutationVariables(event),
    [event],
  );
  const eventUpdateArgs = useMemo(
    () => updateScheduledEventMutationVariables(event, modifyType),
    [event, modifyType],
  );

  const updateEvent = useCallback(
    (callback: (draft: WritableDraft<ScheduledEvent>) => void) => {
      setEvent((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataEvent,
    loading: loadingEvent,
    error: errorEvent,
  } = useGetScheduledEventQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  const dateRangeValue = useMemo<DateRangeInputProps["value"]>(
    () => [
      event.start ? new Date(event.start) : null,
      event.end ? new Date(event.end) : null,
    ],
    [event.end, event.start],
  );
  const showRecurring = useMemo<boolean>(
    () =>
      !id ||
      (modifyType === ScheduledEventModificationType.All &&
        !!dataEvent?.item?.groupId),
    [dataEvent?.item?.groupId, id, modifyType],
  );

  // "Edit all recurring events" edits/deletes the whole series, not just the
  // one occurrence this route happened to be reached through -- SaveToolbar's
  // built-in delete always fires DeleteScheduledEventDocument for a single
  // `id`, which silently deleted only that one occurrence and left the rest
  // of the series behind. Series deletion is a REST-only, groupId-scoped
  // endpoint (see use-delete-scheduled-event-group.ts), so it can't be wired
  // through SaveToolbar's GraphQL-only `documentDelete` prop -- it needs its
  // own control instead.
  const isSeriesEdit =
    modifyType === ScheduledEventModificationType.All &&
    !!dataEvent?.item?.groupId;

  const handleDeleteSeries = useCallback(async () => {
    const groupId = dataEvent?.item?.groupId;
    if (!groupId) {
      return;
    }

    try {
      await deleteGroup(groupId);
      toaster?.show({
        icon: "tick",
        intent: "success",
        message: "Deleted all recurring events",
        timeout: 3000,
      });
      navigate(`/${SidebarEntryPath.scheduler}${search}`);
    } catch (e: unknown) {
      console.error("[SchedulerEdit]: Error: ", e);
      toaster?.show({
        icon: "warning-sign",
        intent: "danger",
        message: "Failed deleting all recurring events",
        timeout: 3000,
      });
    } finally {
      setIsDeleteSeriesAlertOpen(false);
    }
  }, [dataEvent?.item?.groupId, deleteGroup, navigate, search, toaster]);

  useEffect(
    function effectOnArgsCreateChanged() {
      if (argsCreate) {
        setEvent({
          start: moment(argsCreate.start).toDate(),
          end: argsCreate.end ? moment(argsCreate.end).toDate() : undefined,
          organization: "",
          startProcessed: false,
          endProcessed: false,
          timezone: BROWSER_TIMEZONE,
        });
      }
    },
    [argsCreate],
  );

  useEffect(
    function effectOnEditDataChanged() {
      if (dataEvent?.item) {
        setEvent({
          ...dataEvent?.item,
          start: dataEvent?.item?.start
            ? moment(dataEvent?.item?.start).toDate()
            : undefined,
          end: dataEvent?.item?.end
            ? moment(dataEvent?.item?.end).toDate()
            : undefined,
        });
      }
    },
    [dataEvent],
  );

  useUpdateSelectedOrganization(dataEvent?.item?.organization);

  if (loadingEvent) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorEvent) {
    return (
      <Callout intent="danger" title="Failed Loading Event">
        <p>Failed loading event item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingEvent}
        title={
          modifyType === ScheduledEventModificationType.Single
            ? "Event"
            : "Event with All Recurring Events"
        }
        name={event?.name}
      />
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            label="Name"
            labelFor="control-name"
            labelInfo="(required)"
            disabled={disabled}
          >
            <InputGroup
              disabled={disabled}
              id="control-name"
              placeholder="Name"
              value={event.name ?? ""}
              onValueChange={(value) =>
                updateEvent((draft) => {
                  draft.name = value;
                })
              }
            />
          </FormGroup>
          <FormGroup
            disabled={disabled}
            label="Timezone"
            helperText="Times below are interpreted in this timezone"
          >
            <TimezoneSelect
              disabled={disabled}
              fill
              onChange={(timezone) =>
                updateEvent((draft) => {
                  draft.timezone = timezone;
                })
              }
              showLocalTimezone
              value={event.timezone ?? BROWSER_TIMEZONE}
            />
          </FormGroup>
          <FormGroup disabled={disabled}>
            <Switch
              disabled={disabled}
              label="Enable Date Duration with an Ending Action"
              checked={!!event.end}
              onChange={() =>
                updateEvent((draft) => {
                  if (draft.end) {
                    draft.end = undefined;
                    draft.endAction = undefined;
                  } else {
                    draft.end = draft.end ? undefined : moment().endOf("day");
                  }
                })
              }
            />
          </FormGroup>
          {!event.end && (
            <FormGroup label="Date" disabled={disabled}>
              <DateInput
                closeOnSelection
                dateFnsFormat="LLL do, yyyy 'at' K:mm a"
                disabled={disabled}
                fill
                highlightCurrentDay
                onChange={(date) => {
                  updateEvent((draft) => {
                    draft.start = (date && new Date(date)) || undefined;
                    draft.end = undefined;
                    draft.endAction = undefined;
                  });
                }}
                popoverProps={{
                  minimal: true,
                }}
                shortcuts
                timePrecision="second"
                timezone={event.timezone ?? BROWSER_TIMEZONE}
                value={event.start?.toISOString() ?? undefined}
              />
              <SchedulerTimezoneNote date={event.start} zone={event.timezone} />
            </FormGroup>
          )}
          {!!event.end && (
            <FormGroup label="Date Range" disabled={disabled}>
              <DateRangeInput
                allowSingleDayRange
                closeOnSelection
                contiguousCalendarMonths
                dateFnsFormat="LLL do, yyyy 'at' K:mm a"
                disabled={disabled}
                fill
                highlightCurrentDay
                onChange={(range) => {
                  updateEvent((draft) => {
                    draft.start = range[0] ?? undefined;
                    draft.end = range[1] ?? undefined;
                  });
                }}
                popoverProps={{
                  minimal: true,
                }}
                shortcuts
                timePrecision="second"
                value={dateRangeValue}
              />
              <SchedulerTimezoneNote
                date={event.start}
                label="Starts"
                zone={event.timezone}
              />
              <SchedulerTimezoneNote
                date={event.end}
                label="Ends"
                zone={event.timezone}
              />
            </FormGroup>
          )}
        </div>
      </Card>
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            disabled={disabled}
            label={event.end ? "Starting Actions" : "Actions"}
          >
            <SchedulerEditActionList
              disabled={disabled}
              actions={event.startAction}
              onChange={(actions) => {
                updateEvent((draft) => {
                  draft.startAction = actions;
                });
              }}
            />
          </FormGroup>
        </div>
      </Card>
      {!!event.end && (
        <Card elevation={1} className="mt-4">
          <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
            <FormGroup disabled={disabled} label="Ending Actions">
              <SchedulerEditActionList
                disabled={disabled}
                actions={event.endAction}
                onChange={(actions) => {
                  updateEvent((draft) => {
                    draft.endAction = actions;
                  });
                }}
              />
            </FormGroup>
          </div>
        </Card>
      )}
      {showRecurring && (
        <div className={`mt-4 ${EDIT_CARD_WIDTH_CLASS}`}>
          {!id ? (
            <SchedulerRecurrenceEditor
              disabled={disabled}
              onChange={(recurring) =>
                updateEvent((draft) => {
                  draft.recurring = recurring ?? undefined;
                })
              }
              recurring={event.recurring}
              start={event.start}
              timezone={event.timezone}
            />
          ) : (
            event.recurring && (
              <SchedulerRecurrenceSummary
                recurring={event.recurring}
                start={event.start}
              />
            )
          )}
        </div>
      )}
      <Card className="mt-4">
        <div className={`flex items-center gap-2 ${EDIT_CARD_WIDTH_CLASS}`}>
          <div className="flex flex-1">
            <SaveToolbar
              documentCreate={CreateScheduledEventDocument}
              documentDelete={DeleteScheduledEventDocument}
              documentUpdate={UpdateScheduledEventDocument}
              deleteSupported={!isSeriesEdit}
              path={SidebarEntryPath.scheduler}
              isNew={!event?.id}
              refetchQueries={[
                GetScheduledEventDocument,
                GetScheduledEventsDocument,
              ]}
              title="Event"
              variablesCreate={eventCreateArgs}
              variablesUpdate={eventUpdateArgs}
              variablesDelete={{ id: event?.id }}
              onInflight={(inflight) => {
                setDisabled(inflight);
              }}
            />
          </div>
          {isSeriesEdit && (
            <Button
              className="min-w-20"
              disabled={disabled || deletingGroup}
              intent="danger"
              loading={deletingGroup}
              minimal
              onClick={() => setIsDeleteSeriesAlertOpen(true)}
              text="Delete Series"
            />
          )}
        </div>
        {isSeriesEdit && (
          <Alert
            cancelButtonText="Cancel"
            canEscapeKeyCancel
            canOutsideClickCancel
            confirmButtonText="Delete"
            icon="trash"
            intent="danger"
            isOpen={isDeleteSeriesAlertOpen}
            onCancel={() => setIsDeleteSeriesAlertOpen(false)}
            onConfirm={handleDeleteSeries}
          >
            <p>
              Are you sure you want to delete all events in this recurring
              series? This cannot be undone.
            </p>
          </Alert>
        )}
      </Card>
    </div>
  );
}
